import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Images, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api, { formatApiErrorDetail } from "../../api";

const INPUT_CLS =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors";

const CATEGORIES = ["For Sale", "Trade", "Wanted"];

export const NewPostDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ title: "", category: "For Sale", description: "", price: "", contact: "" });
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const urls = photos.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    setPhotos((p) => [...p, ...files]);
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const paths = [];
      for (const f of photos) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        paths.push(data.path);
      }
      const { data } = await api.post("/posts", {
        ...form,
        price: form.price || null,
        contact: form.contact || null,
        images: paths,
      });
      toast.success("Listed on the Trading Post");
      setForm({ title: "", category: "For Sale", description: "", price: "", contact: "" });
      setPhotos([]);
      onOpenChange(false);
      if (onCreated) onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-post-dialog" className="bg-[#121212] border border-white/10 rounded-none text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-3xl">List an Item</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            Post a firearm, part, or accessory for sale or trade. Buyers contact you directly.
          </DialogDescription>
        </DialogHeader>

        <form data-testid="new-post-form" onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 border border-white/10">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`post-category-${c.toLowerCase().replace(" ", "-")}`}
                onClick={() => setForm((f) => ({ ...f, category: c }))}
                className={`py-3 text-xs uppercase tracking-widest transition-colors ${form.category === c ? "bg-primary text-white" : "text-white/50 hover:text-white"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <input data-testid="post-title-input" required minLength={3} value={form.title} onChange={set("title")} placeholder="Title — e.g. Glock 19 Gen 5, LNIB" className={INPUT_CLS} />
          <textarea data-testid="post-description-input" required minLength={3} rows={4} value={form.description} onChange={set("description")} placeholder="Condition, round count, included extras..." className={INPUT_CLS} />
          <div className="grid grid-cols-2 gap-4">
            <input data-testid="post-price-input" value={form.price} onChange={set("price")} placeholder="Price (optional) — $450" className={INPUT_CLS} />
            <input data-testid="post-contact-input" value={form.contact} onChange={set("contact")} placeholder="Contact (optional)" className={INPUT_CLS} />
          </div>

          <div>
            <label
              data-testid="post-photos-label"
              className={`flex items-center justify-center gap-3 border border-dashed border-white/20 px-4 py-6 text-xs uppercase tracking-widest cursor-pointer transition-colors ${photos.length >= 5 ? "text-white/20 cursor-not-allowed" : "text-white/50 hover:border-primary hover:text-primary"}`}
            >
              <Images size={20} weight="duotone" />
              {photos.length >= 5 ? "Max 5 photos" : `Add photos (${photos.length}/5)`}
              <input
                data-testid="post-photos-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                disabled={photos.length >= 5}
                onChange={addPhotos}
                className="hidden"
              />
            </label>
            {previews.length > 0 && (
              <div data-testid="post-photos-preview" className="flex gap-3 mt-3 flex-wrap">
                {previews.map((src, i) => (
                  <div key={src} className="relative group">
                    <img src={src} alt={`Upload ${i + 1}`} className="w-20 h-20 object-cover border border-white/15" />
                    <button
                      type="button"
                      data-testid={`post-photo-remove-${i}`}
                      onClick={() => removePhoto(i)}
                      className="absolute -top-2 -right-2 bg-primary text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove photo"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            data-testid="post-submit-btn"
            disabled={busy}
            className="w-full bg-primary text-white px-8 py-3.5 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50"
          >
            {busy ? "Posting..." : "Post Listing"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
